import { useEffect, useRef } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      remove: (id: string) => void;
      reset: (id: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;

const loadScript = (): Promise<void> => {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  const loading = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      reject(new Error("Security check could not load."));
    };
    document.head.appendChild(script);
  });
  const tracked = loading.catch((error) => {
    scriptPromise = null;
    throw error;
  });
  scriptPromise = tracked;
  return tracked;
};

export type TurnstileState = "loading" | "verified" | "expired" | "error";

interface Props {
  siteKey: string;
  onToken: (token: string) => void;
  onState: (state: TurnstileState) => void;
}

export function Turnstile({ siteKey, onToken, onState }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let widgetId: string | null = null;
    let mounted = true;
    onToken("");
    onState("loading");
    void loadScript()
      .then(() => {
        if (!mounted || !containerRef.current || !window.turnstile) return;
        widgetId = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: "madonna-submit-v1",
          theme: "light",
          size: "flexible",
          appearance: "always",
          callback: (token: string) => {
            onToken(token);
            onState("verified");
          },
          "expired-callback": () => {
            onToken("");
            onState("expired");
          },
          "timeout-callback": () => {
            onToken("");
            onState("expired");
          },
          "error-callback": () => {
            onToken("");
            onState("error");
          },
        });
      })
      .catch(() => {
        if (mounted) onState("error");
      });
    return () => {
      mounted = false;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [onState, onToken, siteKey]);
  return <div className="turnstile" ref={containerRef} aria-label="Security check" />;
}
